import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

// Safety net for cart requests that never report back (see #waitForCartResponse)
const CART_RESPONSE_TIMEOUT = 8000;

// Purchase selection, announced on the section by the selling plan picker
// (assets/selling-plan-picker.js).
const SELLING_PLAN_CHANGE_EVENT = 'selling-plan:change';
const SELLING_PLAN_REQUEST_EVENT = 'selling-plan:request';

// Cadence inside a selling plan name — "Delivery every 2 months" -> 2, "month".
const PLAN_INTERVAL_PATTERN = /(\d+)\s*(day|week|month|year)/i;

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} SellingPlanSelection
 * @property {boolean} isSubscription - Whether a subscription is selected.
 * @property {string|null} planName - Selected plan's name, when subscribing.
 * @property {string|null} purchaseLabel - Label to show when not subscribing.
 * @property {string} priceFormatted - Price of the selection.
 * @property {string|null} compareAtPriceFormatted - Struck-through price, when discounted.
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {HTMLElement | null} */
  #triggerElement = null;

  /** @type {number} */
  #triggerRatio = 0.5;

  /** @type {HTMLElement | null} */
  #footer = null;

  /** @type {number | null} */
  #scrollFrame = null;

  /** @type {boolean} */
  #addInFlight = false;

  /** @type {SellingPlanSelection | null} */
  #sellingPlanSelection = null;

  connectedCallback() {
    super.connectedCallback();

    this.#setupTriggers();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    target?.addEventListener(SELLING_PLAN_CHANGE_EVENT, this.#handleSellingPlanChange, { signal });

    // The picker broadcasts as it connects, which may be before this element is
    // upgraded — ask for the selection again rather than waiting for the shopper
    // to change something.
    target?.dispatchEvent(new CustomEvent(SELLING_PLAN_REQUEST_EVENT));

    this.#getInitialQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
    if (this.#scrollFrame != null) {
      cancelAnimationFrame(this.#scrollFrame);
    }
  }

  /**
   * Picks how the bar is revealed.
   *
   * When `data-trigger-selector` resolves to an element in the section, the bar
   * appears as soon as that element has been scrolled past by `data-trigger-ratio`
   * of its own height — the PDP hero points it at the real add to cart button at
   * 100%, so the bar only takes over once that button is off the top of the screen.
   * Otherwise it falls back to watching the buy buttons.
   */
  #setupTriggers() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');

    // In themes migrated from 2.0, the footer element doesn't exist
    this.#footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');

    const triggerElement = this.#resolveTriggerElement();
    if (triggerElement) {
      this.#setupScrollTrigger(triggerElement);
    } else {
      this.#setupIntersectionObserver(productForm);
    }
  }

  /**
   * Resolves the opt-in scroll trigger element, scoped to this section so a
   * matching element elsewhere on the page can't hijack the trigger.
   * @returns {HTMLElement | null}
   */
  #resolveTriggerElement() {
    const selector = this.dataset.triggerSelector;
    if (!selector) return null;

    const section = this.closest('.shopify-section');
    return /** @type {HTMLElement | null} */ (section?.querySelector(selector) ?? null);
  }

  /**
   * Reveals the bar once the trigger element is scrolled past its ratio point.
   * @param {HTMLElement} triggerElement
   */
  #setupScrollTrigger(triggerElement) {
    this.#triggerElement = triggerElement;

    const ratio = Number(this.dataset.triggerRatio);
    if (Number.isFinite(ratio) && ratio >= 0 && ratio <= 1) this.#triggerRatio = ratio;

    const { signal } = this.#abortController;
    window.addEventListener('scroll', this.#handleScroll, { passive: true, signal });
    window.addEventListener('resize', this.#handleScroll, { passive: true, signal });

    this.#evaluateScrollTrigger();
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   * @param {HTMLElement} productForm
   */
  #setupIntersectionObserver(productForm) {
    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    const footer = this.#footer;
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          // Element is above viewport - show sticky bar
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
  }

  /**
   * Throttles scroll/resize work to one evaluation per frame.
   */
  #handleScroll = () => {
    if (this.#scrollFrame != null) return;

    this.#scrollFrame = requestAnimationFrame(() => {
      this.#scrollFrame = null;
      this.#evaluateScrollTrigger();
    });
  };

  /**
   * Height of the sticky site header, so the bar reveals as the trigger point
   * slides behind the header rather than off the top of the viewport.
   * @returns {number}
   */
  #getStickyHeaderOffset() {
    const header = document.querySelector('.header-section');
    if (!header) return 0;

    return Math.max(0, header.getBoundingClientRect().bottom);
  }

  /**
   * The trigger element, re-resolved if a variant update replaced the node it was
   * cached from. A detached node reports an all-zero rect, which would read as
   * "scrolled past" and pin the bar open for the whole page.
   * @returns {HTMLElement | null}
   */
  #getTriggerElement() {
    if (this.#triggerElement?.isConnected) return this.#triggerElement;

    this.#triggerElement = this.#resolveTriggerElement();
    return this.#triggerElement;
  }

  /**
   * Whether the bar should currently be visible in scroll-trigger mode.
   * @returns {boolean}
   */
  #shouldBeVisible() {
    const triggerElement = this.#getTriggerElement();
    if (!triggerElement) return false;

    const rect = triggerElement.getBoundingClientRect();
    const triggerPoint = rect.top + rect.height * this.#triggerRatio;
    if (triggerPoint > this.#getStickyHeaderOffset()) return false;

    // Step aside once the footer is close to entering the viewport
    if (this.#footer && this.#footer.getBoundingClientRect().top - 200 < window.innerHeight) {
      return false;
    }

    return true;
  }

  #evaluateScrollTrigger() {
    const shouldBeVisible = this.#shouldBeVisible();

    if (shouldBeVisible && !this.#isStuck) {
      this.#showStickyBar();
    } else if (!shouldBeVisible && this.#isStuck) {
      this.#hideStickyBar();
    }
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar.
   *
   * The bar has no form of its own — it puppets the real add-to-cart button in the
   * product form. Two opt-in attributes change what happens next:
   *
   * - `data-show-loading="true"` keeps the button in a spinner state until the cart
   *   request resolves, instead of confirming optimistically on click.
   * - `data-open-cart-drawer="true"` opens the cart drawer once the item is in the
   *   cart (independent of the theme's global `auto_open_cart_drawer` setting).
   *
   * With neither set, the original fire-and-forget behaviour is preserved.
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton || this.#addInFlight) return;

    const showLoading = this.dataset.showLoading === 'true';
    const openDrawer = this.dataset.openCartDrawer === 'true';

    this.#addInFlight = true;
    if (showLoading) this.#setLoadingState(true);

    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();

    if (!showLoading && !openDrawer) {
      this.#addInFlight = false;
      await this.#playAddedFeedback();
      return;
    }

    const added = await this.#waitForCartResponse();

    if (showLoading) this.#setLoadingState(false);
    this.#addInFlight = false;

    if (!added) return;

    if (openDrawer) this.#openCartDrawer();

    // The drawer covers the header cart icon, so the fly-to-cart animation would
    // land behind it — the checkmark alone confirms the add in that case.
    void this.#playAddedFeedback({ flyToCart: !openDrawer });
  };

  /**
   * Toggles the button's in-flight state. `data-loading` hides the label and
   * reveals the spinner in CSS; the button is left enabled so variant updates
   * can't misread it as unavailable, and pointer events are killed in CSS.
   * @param {boolean} isLoading
   */
  #setLoadingState(isLoading) {
    const { addToCartButton } = this.refs;
    if (!addToCartButton) return;

    if (isLoading) {
      addToCartButton.dataset.loading = 'true';
      addToCartButton.setAttribute('aria-busy', 'true');
    } else {
      addToCartButton.removeAttribute('data-loading');
      addToCartButton.removeAttribute('aria-busy');
    }
  }

  /**
   * Resolves once the puppeted form reports back: `true` when the item made it
   * into the cart, `false` on an error.
   *
   * The timeout is the safety net for paths where the form bails out before it
   * ever posts (quantity rules, an invalid form), which emit no cart event at all.
   * @returns {Promise<boolean>}
   */
  #waitForCartResponse() {
    return new Promise((resolve) => {
      const controller = new AbortController();
      /** @type {number} */
      let timeout;

      /** @param {boolean} added */
      const settle = (added) => {
        clearTimeout(timeout);
        controller.abort();
        resolve(added);
      };

      const { signal } = controller;

      document.addEventListener(
        ThemeEvents.cartUpdate,
        (event) => {
          const data = /** @type {CustomEvent} */ (event).detail?.data ?? {};

          // Ignore cart activity from elsewhere on the page (e.g. a drawer quantity change)
          if (data.source && data.source !== 'product-form-component') return;
          if (data.productId && data.productId !== this.dataset.productId) return;

          settle(!data.didError);
        },
        { signal }
      );

      document.addEventListener(ThemeEvents.cartError, () => settle(false), { signal });

      timeout = setTimeout(() => settle(false), CART_RESPONSE_TIMEOUT);
    });
  }

  /**
   * Opens the cart drawer, skipping the copy rendered inside the mobile header
   * drawer — it comes first in the DOM but is hidden until that drawer is open.
   */
  #openCartDrawer() {
    const drawers = /** @type {NodeListOf<HTMLElement & { open?: () => void }>} */ (
      document.querySelectorAll('cart-drawer-component')
    );

    const drawer = Array.from(drawers).find((candidate) => !candidate.closest('header-drawer')) ?? drawers[0];
    if (typeof drawer?.open !== 'function') return;

    drawer.open();
  }

  /**
   * Shows the "added" confirmation on the sticky button, optionally flying the
   * product image up to the header cart icon.
   * @param {{ flyToCart?: boolean }} [options]
   */
  async #playAddedFeedback({ flyToCart = true } = {}) {
    const { addToCartButton, productImage } = this.refs;
    if (!addToCartButton) return;

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    /** @type {HTMLElement[]} */
    const animated = [addToCartButton];
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (flyToCart && cartIcon && productImage) {
      const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

      flyToCartElement.classList.add('fly-to-cart--sticky');
      flyToCartElement.style.setProperty('background-image', `url(${productImage.src})`);
      flyToCartElement.useSourceSize = 'true';
      flyToCartElement.source = productImage;
      flyToCartElement.destination = cartIcon;

      document.body.appendChild(flyToCartElement);
      animated.push(flyToCartElement);
    }

    await onAnimationEnd(animated);

    this.#resetTimeout = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');
    }, 800);
  }

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();

    // The morph above replaced the bar with server-rendered markup, which knows
    // nothing about the selected purchase option — put the selection back.
    this.#applySellingPlanSelection();
  };

  /**
   * Adopts the purchase selection announced by the selling plan picker.
   * @param {CustomEvent} event
   */
  #handleSellingPlanChange = (event) => {
    this.#sellingPlanSelection = event.detail ?? null;
    this.#applySellingPlanSelection();
  };

  /**
   * Renders the current selection into the bar: the plan label under the title,
   * the price (with the undiscounted price struck through), and the button total.
   *
   * The bar has no pricing logic of its own on purpose — the picker owns which
   * price applies, so the two can never disagree.
   */
  #applySellingPlanSelection() {
    const selection = this.#sellingPlanSelection;
    if (!selection) return;

    this.#updatePlanLabel(selection);
    this.#updatePriceDisplay(selection);
    this.#updateCtaPrice(selection);
  }

  /**
   * @param {SellingPlanSelection} selection
   */
  #updatePlanLabel({ isSubscription, planName, purchaseLabel }) {
    const label = isSubscription ? this.#buildSubscriptionLabel(planName) : purchaseLabel;

    // Nothing to say (a plan with no parsable name, or a picker without a
    // one-time label) leaves whatever the server rendered in place.
    if (!label) return;

    for (const element of this.querySelectorAll(
      '.pdp-v2-sticky__variant--desktop, .pdp-v2-sticky__variant--mobile'
    )) {
      element.textContent = label;
    }
  }

  /**
   * Fills the section's label template with the plan's cadence, so
   * "Delivery every 2 months" reads as "Every 2 Month Filter Subscription".
   * Falls back to the plan's own name when either piece is missing.
   *
   * @param {string | null} planName
   * @returns {string}
   */
  #buildSubscriptionLabel(planName) {
    const template = this.dataset.subscriptionLabel;
    if (!planName || !template) return planName ?? '';

    const match = planName.match(PLAN_INTERVAL_PATTERN);
    if (!match) return planName;

    const [, count, rawUnit] = match;
    const unit = rawUnit.toLowerCase();

    return template.replace('[interval]', `${count} ${unit.charAt(0).toUpperCase()}${unit.slice(1)}`);
  }

  /**
   * Swaps the prices rendered by snippets/price.liquid, which only ever compares a
   * variant's price against its compare-at price. Both the desktop and the mobile
   * copy of the price are updated.
   *
   * @param {SellingPlanSelection} selection
   */
  #updatePriceDisplay({ priceFormatted, compareAtPriceFormatted }) {
    if (!priceFormatted) return;

    for (const container of this.querySelectorAll('[ref="priceContainer"]')) {
      const regular = container.querySelector('.price__regular');
      const sale = container.querySelector('.price__sale');
      if (!regular || !sale) continue;

      const regularPrice = regular.querySelector('.price');
      const salePrice = sale.querySelector('.price-item--sale.price');
      const saleCompare = sale.querySelector('.price-item--regular.compare-at-price');

      if (regularPrice) regularPrice.textContent = priceFormatted;
      if (salePrice) salePrice.textContent = priceFormatted;
      if (saleCompare && compareAtPriceFormatted) saleCompare.textContent = compareAtPriceFormatted;

      // The sale row is the one carrying the struck-through price.
      regular.classList.toggle('price__hidden', Boolean(compareAtPriceFormatted));
      sale.classList.toggle('price__hidden', !compareAtPriceFormatted);
    }
  }

  /**
   * Keeps the total on the button in step with the selection. Only the price span
   * is touched, so the button's copy and its quantity suffix stay put.
   *
   * @param {SellingPlanSelection} selection
   */
  #updateCtaPrice({ priceFormatted }) {
    const priceElement = this.querySelector('.pdp-v2-sticky__cta-price');
    if (!priceElement || !priceFormatted) return;

    priceElement.textContent = priceFormatted;
  }

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
